import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AlarmThresholdCouplingControl from './AlarmThresholdCoupling';
import DashboardTemperatureCouplingControl from './DashboardTemperatureCoupling';
import {
  CouplingAlternatesSection,
  CouplingAlternatesHeader,
  CouplingAlternatesTitle,
  CouplingAlternatesHint,
  CouplingAlternateRow,
  CouplingAlternateRowContent,
  CouplingAlternatesDivider
} from './AlarmThresholdCoupling/styled';

const EventCouplingAlternates = ({
  event,
  alarmThresholds,
  fetchingAlarms,
  dashboardCouplingEligibility,
  fetchingDashboardEligibility,
  onAlarmCouplingChange,
  onDashboardCouplingChange,
  temperatureOnly,
  vibrationDirectionPreference,
  user
}) => {
  const { t } = useTranslation();
  const coupledToAlarm = !!event.coupling_config;
  const coupledToDashboard = !!(
    event.dashboard_coupling_config && event.dashboard_coupling_config.enabled
  );
  const dashboardEligible = !!(
    dashboardCouplingEligibility && dashboardCouplingEligibility.eligible
  );
  const showDashboardCouplingOption = (
    fetchingDashboardEligibility || dashboardEligible || coupledToDashboard
  );

  return (
    <CouplingAlternatesSection>
      <CouplingAlternatesHeader>
        <CouplingAlternatesTitle>
          {t('sensors.eventDetection.couplingSource.title')}
        </CouplingAlternatesTitle>
        <CouplingAlternatesHint>
          {t('sensors.eventDetection.couplingSource.hint')}
        </CouplingAlternatesHint>
      </CouplingAlternatesHeader>

      <CouplingAlternateRow
        selected={coupledToAlarm}
        dimmed={coupledToDashboard && !coupledToAlarm}
      >
        <CouplingAlternateRowContent blockInteraction={coupledToDashboard && !coupledToAlarm}>
          <AlarmThresholdCouplingControl
            embedded
            coupledToAlarmThresholds={coupledToAlarm}
            couplingConfig={event.coupling_config || null}
            alarmThresholds={alarmThresholds}
            fetchingAlarms={fetchingAlarms}
            conditions={event.conditions}
            onChange={onAlarmCouplingChange}
            temperatureOnly={temperatureOnly}
            vibrationDirectionPreference={vibrationDirectionPreference}
            dashboardCouplingEnabled={coupledToDashboard}
            otherCouplingActive={coupledToDashboard}
            user={user}
          />
        </CouplingAlternateRowContent>
      </CouplingAlternateRow>

      {showDashboardCouplingOption && (
        <>
          <CouplingAlternatesDivider>
            {t('sensors.eventDetection.couplingSource.or')}
          </CouplingAlternatesDivider>

          <CouplingAlternateRow
            selected={coupledToDashboard}
            dimmed={coupledToAlarm && !coupledToDashboard}
          >
            <CouplingAlternateRowContent blockInteraction={coupledToAlarm && !coupledToDashboard}>
              <DashboardTemperatureCouplingControl
                embedded
                coupledToDashboard={coupledToDashboard}
                dashboardCouplingEligible={dashboardEligible}
                dashboardCouplingReason={
                  dashboardCouplingEligibility && dashboardCouplingEligibility.reason
                }
                fetchingEligibility={fetchingDashboardEligibility}
                dashboardLimits={
                  dashboardCouplingEligibility && dashboardCouplingEligibility.limits
                }
                onChange={onDashboardCouplingChange}
                alarmCouplingEnabled={coupledToAlarm}
                otherCouplingActive={coupledToAlarm}
              />
            </CouplingAlternateRowContent>
          </CouplingAlternateRow>
        </>
      )}
    </CouplingAlternatesSection>
  );
};

EventCouplingAlternates.propTypes = {
  event: PropTypes.object.isRequired,
  alarmThresholds: PropTypes.object,
  fetchingAlarms: PropTypes.bool,
  dashboardCouplingEligibility: PropTypes.object,
  fetchingDashboardEligibility: PropTypes.bool,
  onAlarmCouplingChange: PropTypes.func.isRequired,
  onDashboardCouplingChange: PropTypes.func.isRequired,
  temperatureOnly: PropTypes.bool,
  vibrationDirectionPreference: PropTypes.number,
  user: PropTypes.object
};

EventCouplingAlternates.defaultProps = {
  alarmThresholds: null,
  fetchingAlarms: false,
  dashboardCouplingEligibility: null,
  fetchingDashboardEligibility: false,
  temperatureOnly: false,
  vibrationDirectionPreference: 1,
  user: null
};

export default EventCouplingAlternates;
